import React, { useEffect, useState } from 'react';

import { CrewList } from '../../../features/crew/components/crew-list';
import { CrewGroupingDropdown } from '../../../features/crew/components/crew-grouping-dropdown';
import { CrewMemberModal } from '../../../features/crew/components/crew-member.modal';
import { AssignCrewMemberModal } from '../../../features/crew/components/assign-crew-member.modal';
import {
  deleteCrewMember,
  fetchCrew,
  saveCrewMemberField,
} from '../../../features/crew/stores/crew.slice';
import { TeamHeader } from '../../../features/teams/components/team-screen.header';
import { StepDrawer } from '../../../features/projects/components/step.drawer';
import { catchError } from '../../../core/catch-error';
import { ToolBar, Tools } from '../../../components/toolbar/toolbar';

import type { CrewGroupBy } from '../../../features/crew/components/crew-list';
import type { CrewMember } from '../../../app/entities/crew-member';
import type { Project } from '../../../app/entities/project';
import type { Step } from '../../../app/entities/step';

export type CrewPageProps = {
  project: Project;
  step: Step;
};

/**
 * The Crew step and `Resources > Crew` render the same component over the same data. The step
 * survives for its tasks, comments, dates, assignee and status — crew itself is no longer owned
 * by it.
 */
export const TeamPage: React.FC<CrewPageProps> = ({ project, step }) => {
  const [editing, setEditing] = useState<CrewMember | null>(null);
  const [assigning, setAssigning] = useState<CrewMember | null>(null);
  // `undefined` means the create modal is closed; an object (possibly empty) means it is open,
  // pre-filled from a group header's plus button.
  const [createDefaults, setCreateDefaults] = useState<Record<string, string> | undefined>(
    undefined,
  );
  const [groupBy, setGroupBy] = useState<CrewGroupBy>(null);

  useEffect(() => {
    fetchCrew(project._id);
  }, [project._id]);

  // The "Ungrouped" bucket's value is `''` — pre-filling `{ department: '' }` would be worse than
  // no default at all, so an empty group value (or no active `groupBy`) opens the form blank.
  const handleAddToGroup = (value: string) => {
    setCreateDefaults(groupBy && value ? { [groupBy]: value } : {});
  };

  return (
    <React.Fragment>
      <TeamHeader
        step={step}
        canValidateStep={project.crewMembers.length > 0}
        onCreate={() => setCreateDefaults({})}
      />

      <StepDrawer step={step} />

      {/* This page has no other toolbar controls today — grouping is the only one, so it sits
          alone in `Tools`, matching `Resources > Crew`'s right-hand group. */}
      <ToolBar>
        <div />

        <Tools>
          <CrewGroupingDropdown value={groupBy} onChange={setGroupBy} />
        </Tools>
      </ToolBar>

      {/* Crew records are this page's subject, so it offers the row actions — the same two the
          `Resources > Crew` route offers, over the same data. */}
      <CrewList
        members={project.crewMembers}
        groupBy={groupBy}
        onEdit={setEditing}
        onDelete={(member) =>
          deleteCrewMember(member).catch((e) =>
            catchError(e, 'Could not delete this crew member. Try again.'),
          )
        }
        onAssign={setAssigning}
        onAddToGroup={handleAddToGroup}
        onCreate={() => setCreateDefaults({})}
        onUpdate={(member, update) =>
          saveCrewMemberField(project, member, update).catch((e) =>
            catchError(e, 'Could not save this change. Try again.'),
          )
        }
        departments={project.crewDepartments}
        teams={project.crewTeams}
      />

      {editing && (
        <CrewMemberModal
          project={project}
          member={editing}
          onCancel={() => setEditing(null)}
          onClose={() => setEditing(null)}
        />
      )}

      {assigning && (
        <AssignCrewMemberModal
          project={project}
          member={assigning}
          onCancel={() => setAssigning(null)}
          onClose={() => setAssigning(null)}
        />
      )}

      {createDefaults && (
        <CrewMemberModal
          project={project}
          defaults={createDefaults}
          onCancel={() => setCreateDefaults(undefined)}
          onClose={() => setCreateDefaults(undefined)}
        />
      )}
    </React.Fragment>
  );
};
